import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'sonner';

import { VStack } from '@/components/ui/layout';
import { Separator } from '@/components/ui/separator';

import { useBranding, useFormRevision } from '@utils/api';
import { Form } from '@utils/api/aboboApiTypes';
import { ErrorLanding } from '@components/ErrorLanding';
import Loading from '@components/Loading';
import PreviewBanner from '@components/PreviewBanner';
import PreviewWatermark from '@components/PreviewWatermark';

// Lazy load FormWizard to match the regular form route pattern
const FormWizard = lazy(() => import('@components/FormWizard'));

type PreviewSearch = {
  token?: string;
};

interface PreviewTokenPayload {
  formId: string;
  revisionId: string;
  revisionVersion: number;
  isDraft: boolean;
  formTitle: string;
  clientSlug: string;
  exp: number;
  sub: string;
  aud: string;
}

export const Route = createFileRoute('/forms/preview/$id')({
  component: FormPreview,
  validateSearch: (search: Record<string, unknown>): PreviewSearch => {
    return {
      token: search.token as string | undefined,
    };
  },
});

function FormPreview() {
  const { id: formId } = useParams({ from: '/forms/preview/$id' });
  const { token } = useSearch({ from: '/forms/preview/$id' });
  const { t } = useTranslation();
  const [isComplete, setIsComplete] = useState(false);

  // Decode and validate the preview token
  const tokenPayload = useMemo(() => {
    if (!token) return null;

    try {
      const decoded = jwtDecode<PreviewTokenPayload>(token);

      // Check if token has expiration and is not expired
      if (!decoded.exp || decoded.exp * 1000 < Date.now()) {
        return null;
      }

      // Verify this is a preview token
      if (decoded.aud !== 'form_preview') {
        return null;
      }

      // Verify the form ID matches
      if (decoded.formId !== formId) {
        return null;
      }

      return decoded;
    } catch {
      // Token decode failures are handled by showing ErrorLanding to the user
      return null;
    }
  }, [token, formId]);

  const { branding, isLoading: isBrandingLoading } = useBranding();
  const { formRevision, isLoading: isRevisionLoading, isError: isRevisionError } = useFormRevision(
    tokenPayload?.revisionId,
    token
  );

  // Create a Form-like object that FormWizard expects
  const form = useMemo((): Form | null => {
    if (!formRevision || !tokenPayload) return null;
    return {
      id: tokenPayload.formId,
      lastPublishedRevision: formRevision,
    } as Form;
  }, [formRevision, tokenPayload]);

  const handleClose = () => {
    window.close();
    // window.close() may fail silently if the window wasn't opened by JS
    setTimeout(() => {
      toast.info('Please close this tab manually');
    }, 100);
  };

  const handlePreviewComplete = async () => {
    setIsComplete(true);
  };

  // No token or invalid token
  if (!token || !tokenPayload) {
    return (
      <ErrorLanding
        heading="Preview Link Invalid"
        subHeading="This preview link has expired or is invalid."
        description="Return to the form editor and click 'Preview' to generate a new link."
        footer=""
      />
    );
  }

  if (isBrandingLoading || isRevisionLoading) {
    return <Loading />;
  }

  if (isRevisionError || !form) {
    return (
      <ErrorLanding
        heading={t('errorPage.general.heading')}
        subHeading="Could not load form for preview."
        description="Please try generating a new preview link from the form editor."
        footer=""
      />
    );
  }

  return (
    <>
      <PreviewBanner
        formTitle={tokenPayload.formTitle}
        revisionVersion={tokenPayload.revisionVersion}
        isDraft={tokenPayload.isDraft}
        onClose={handleClose}
      />

      <div
        className="min-h-svh flex flex-row overflow-y-auto px-2 py-4 pt-20 w-full items-center justify-center transition-opacity duration-300"
        style={{
          backgroundColor: branding?.backgroundColor,
          backgroundImage: `radial-gradient(circle at center, ${branding?.backgroundColor}, transparent 30%)`,
          opacity: form ? 1 : 0,
        }}
      >
        {isComplete ? (
          <div className="min-h-[50vh] flex items-center justify-center p-8">
            <PreviewWatermark>
              <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
                {/* Success icon */}
                <div
                  className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'oklch(0.759 0.167 70.67 / 0.15)' }}
                >
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: 'oklch(0.759 0.167 70.67)' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                  Preview Complete!
                </h3>
                <p className="text-gray-600 mb-1">
                  Your form is working correctly.
                </p>
                <p className="text-sm text-gray-400 mb-6">
                  No data was saved. This was a preview only.
                </p>

                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => setIsComplete(false)}
                    className="w-full px-6 py-2 text-white rounded-lg font-medium transition-colors hover:opacity-90"
                    style={{ backgroundColor: 'oklch(0.759 0.167 70.67)' }}
                  >
                    Restart Preview
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="w-full px-6 py-3 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                  >
                    Close Preview
                  </button>
                </div>

                {/* AboboTech logo */}
                <div className="mt-8 pt-6 border-t border-gray-100 flex items-end justify-center gap-2">
                  <span className="text-xs">Powered by</span>
                  <img
                    src="/assets/abobo.svg"
                    alt="AboboTech"
                    className="h-4"
                  />
                </div>
              </div>
            </PreviewWatermark>
          </div>
        ) : (
          <PreviewWatermark>
            <VStack className="text-center w-full max-w-lg bg-white p-6 rounded-2xl">
              {branding?.logo && (
                <img
                  src={branding.logo}
                  alt={`${branding.name}'s Logo`}
                  className="p-1 object-contain w-full h-24"
                />
              )}
              <Separator className="mt-4" />
              <Suspense fallback={<Loading />}>
                <FormWizard
                  form={form}
                  onComplete={handlePreviewComplete}
                  onSave={async () => {
                    // No-op for preview mode - don't save anything
                  }}
                />
              </Suspense>
            </VStack>
          </PreviewWatermark>
        )}
      </div>
    </>
  );
}
